import { useApolloClient } from '@apollo/client';
import { TFunction } from 'i18next';
import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { useAgent, useFeature } from '@treams/treams-hooks';
import {
	AgentChatDocument,
	AgentChatMessageFragment,
	AgentChatMessageRole,
	AgentChatQuery,
	AgentChatsQuery,
	FeatureName,
	useAgentChatQuery,
} from '@treams/treams-network';

import { estimatePromptCostUsd } from '../estimateAgentChatCost';
import { AgentChatThread } from './AgentChatThread';
import type { AgentChatSubject } from './AgentChatThread';

const CHAT_TITLE_MAX_LENGTH = 80;

function deriveChatTitle(messages: AgentChatMessageFragment[], t: TFunction): string {
	const firstUserMessage = messages.find(
		(message) => message.role === AgentChatMessageRole.User,
	)?.content;

	const normalized = firstUserMessage?.replace(/\s+/g, ' ').trim();
	if (!normalized) {
		return t('aiAgent.newChat');
	}

	if (normalized.length <= CHAT_TITLE_MAX_LENGTH) {
		return normalized;
	}

	return `${normalized.slice(0, CHAT_TITLE_MAX_LENGTH - 1)}…`;
}

export function AgentChatDetail(props: {
	chatID?: string;
	chats?: AgentChatsQuery['agentChats'];
	onChatPersisted: () => Promise<unknown>;
}) {
	const { chatID, onChatPersisted } = props;

	const { t, i18n } = useTranslation();
	const history = useHistory();
	const client = useApolloClient();
	const hasAdvancedAgent = useFeature(FeatureName.AdvancedAgent);
	const persistedChatIDRef = useRef<string>();
	const {
		send,
		messages: streamingMessages,
		chatID: streamedChatID,
		isStreaming,
		error,
		reset,
	} = useAgent();

	const [draft, setDraft] = useState('');
	const [pendingMessages, setPendingMessages] = useState<AgentChatMessageFragment[]>([]);

	const chatQuery = useAgentChatQuery({
		variables: { id: chatID! },
		skip: !chatID,
	});

	useEffect(() => {
		if (isStreaming || !streamedChatID) {
			return;
		}

		if (!chatID) {
			if (persistedChatIDRef.current === streamedChatID) {
				return;
			}
			persistedChatIDRef.current = streamedChatID;

			const messages = [...pendingMessages, ...streamingMessages];
			const now = new Date();

			// Pre-seed the cache with the messages to avoid flicker on route change
			client.cache.writeQuery<AgentChatQuery>({
				query: AgentChatDocument,
				variables: { id: streamedChatID },
				data: {
					agentChat: {
						__typename: 'AgentChat',
						id: streamedChatID,
						title: deriveChatTitle(messages, t),
						locale: i18n.language,
						createdAt: now,
						updatedAt: now,
						messages: messages.map((message) => ({
							__typename: 'AgentChatMessage' as const,
							id: message.id,
							role: message.role,
							content: message.content ?? null,
							toolCallId: message.toolCallId ?? null,
							createdAt: message.createdAt,
							toolCalls:
								message.toolCalls?.map((toolCall) => ({
									__typename: 'AgentChatToolCall' as const,
									id: toolCall.id,
									name: toolCall.name,
									arguments: toolCall.arguments ?? null,
								})) ?? null,
						})),
						usage: null,
					},
				},
			});

			onChatPersisted().then(() => history.replace(`/assistant/${streamedChatID}`));
			return;
		}

		chatQuery.refetch().then(() => {
			setPendingMessages([]);
			reset();
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isStreaming, streamedChatID]);

	const messages = useMemo(
		() => [...(chatQuery.data?.agentChat.messages ?? []), ...pendingMessages, ...streamingMessages],
		[chatQuery.data?.agentChat.messages, pendingMessages, streamingMessages],
	);

	const usage = chatQuery.data?.agentChat.usage;
	const estimatedCost = useMemo(() => (usage ? estimatePromptCostUsd(usage) : undefined), [usage]);
	const subject = useMemo<AgentChatSubject>(() => {
		if (!chatID) {
			return null;
		}

		return props.chats?.find((chat) => chat.id === chatID)?.subject ?? null;
	}, [chatID, props.chats]);

	async function submitMessage(rawMessage: string, subjectID?: string) {
		const message = rawMessage.trim();
		if (!message || isStreaming) {
			return;
		}

		const createdAt = new Date();
		setDraft('');
		setPendingMessages((prev) => [
			...prev,
			{
				id: `pending-user-${createdAt.getTime()}`,
				role: AgentChatMessageRole.User,
				content: message,
				createdAt,
			},
		]);

		await send(message, { chatID, locale: i18n.language, subjectID });
	}

	function handleSubmit(event: FormEvent) {
		event.preventDefault();
		void submitMessage(draft);
	}

	return (
		<AgentChatThread
			messages={messages}
			isLoading={!!chatID && !chatQuery.data && !chatQuery.error}
			isStreaming={isStreaming}
			error={error ?? chatQuery.error?.message}
			draft={draft}
			onDraftChange={setDraft}
			onSubmit={handleSubmit}
			onSelectSuggestion={submitMessage}
			onStartOver={() => history.push('/assistant/new')}
			showComposer={hasAdvancedAgent}
			estimatedCost={estimatedCost}
			subject={subject}
		/>
	);
}
